import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FolderOpen, Target, BarChart3, ArrowLeft, Calendar, Users as UsersIcon } from "lucide-react";
import { useApi } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { getGoalsByProject } from "../../../services/goalsPerformanceApi";
import ProjectAnalytics from "../../../components/ProjectAnalytics/ProjectAnalytics";
import { DASHBOARD_BASE } from "../../../config/featureFlags";
import { PageShell } from "../components/PageShell";
import { Card, Button, Badge, Avatar } from "../components/primitives";
import { DataTable, EmptyState } from "../components/DataTable";
import { TabPanel } from "../components/Tabs";
import { Shimmer } from "../components/StatCard";

const statusTone = (s) => (s === "active" ? "positive" : s === "archived" ? "neutral" : s === "completed" ? "info" : "warning");
const goalStatusTone = (s) => (s === "APPROVED" ? "positive" : s === "PENDING" ? "warning" : "neutral");

export default function ProjectDetailV2() {
  const t = useDashTheme();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  // Base path of the V2 shell (flag-aware).
  const base = DASHBOARD_BASE;

  const [project, setProject] = React.useState(location.state?.project || null);
  const [goals, setGoals] = React.useState([]);
  const [loading, setLoading] = React.useState(!location.state?.project);
  const [goalsLoading, setGoalsLoading] = React.useState(true);
  const [tab, setTab] = React.useState("goals");

  // Fetch project (only if not handed in via navigation state).
  React.useEffect(() => {
    if (project) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/v1/projects/${projectId}`);
        if (res.data.success) setProject(res.data.data.project || res.data.data);
      } catch (_) {
        try {
          const list = await api.get(`/api/v1/projects?page=1&limit=100`);
          if (list.data.success) {
            const found = list.data.data.projects?.find((p) => p._id === projectId);
            if (found) setProject(found);
          }
        } catch (__) {}
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Goals for this project.
  React.useEffect(() => {
    (async () => {
      setGoalsLoading(true);
      try {
        const res = await getGoalsByProject(projectId);
        if (res.success) setGoals(res.data?.goals || []);
      } catch (_) {} finally { setGoalsLoading(false); }
    })();
  }, [projectId]);

  if (loading) {
    return (
      <PageShell eyebrow="Workspace · Project" title="Loading project…" icon={<FolderOpen size={22} />}>
        <Card padding={24}>
          <Shimmer w="40%" h={20} /><div style={{ height: 14 }} /><Shimmer w="100%" h={12} /><div style={{ height: 8 }} /><Shimmer w="80%" h={12} />
        </Card>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell eyebrow="Workspace · Project" title="Project not found" icon={<FolderOpen size={22} />}>
        <Card padding={0}>
          <EmptyState icon={<FolderOpen size={22} />} title="Project not found"
            description="This project may have been removed or you don’t have access to it."
            action={<Button variant="primary" leftIcon={<ArrowLeft size={15} />} onClick={() => navigate(`${base}/projects`)}>Back to projects</Button>} />
        </Card>
      </PageShell>
    );
  }

  const members = project.teamMembers || [];

  return (
    <PageShell
      eyebrow="Workspace · Project"
      title={project.name}
      subtitle={project.description || "No description provided"}
      icon={<FolderOpen size={22} />}
      tabs={[
        { key: "goals", label: "Goals", icon: <Target size={15} />, count: goals.length },
        { key: "analytics", label: "Analytics", icon: <BarChart3 size={15} /> },
      ]}
      activeTab={tab} onTabChange={setTab} tabLayoutId="projDetailTab"
      topRight={<Badge tone={statusTone(project.status)} dot>{project.status || "active"}</Badge>}
      actions={<Button variant="secondary" leftIcon={<ArrowLeft size={15} />} onClick={() => navigate(`${base}/projects`)}>Back to projects</Button>}
    >
      {/* Meta strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: t.s[4], marginBottom: t.s[6] }}>
        <MetaCard t={t} icon={<Calendar size={16} />} label="Start date" value={project.startDate ? new Date(project.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} accent={t.accents[0]} />
        <MetaCard t={t} icon={<Calendar size={16} />} label="End date" value={project.endDate ? new Date(project.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"} accent={t.accents[1]} />
        <MetaCard t={t} icon={<UsersIcon size={16} />} label="Team members" value={members.length} accent={t.accents[2]} />
        <MetaCard t={t} icon={<Target size={16} />} label="Goals" value={goals.length} accent={t.accents[3]} />
      </div>

      {/* Goals tab */}
      <TabPanel activeKey={tab} tabKey="goals">
        <Card padding={0}>
          <DataTable
            loading={goalsLoading}
            rows={goals.map((g, i) => ({ ...g, _idx: i + 1 }))}
            rowKey={(g, i) => g._id ?? i}
            emptyState={<EmptyState icon={<Target size={22} />} title="No goals found" description="No goals have been assigned to this project yet." />}
            columns={[
              { key: "idx", header: "#", width: 48, render: (g) => <span style={{ color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>{g._idx}</span> },
              { key: "name", header: "Goal", render: (g) => (
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: t.font.size.md, fontWeight: 600, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.name}</div>
                  {g.description && <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.description}</div>}
                </div>
              ) },
              { key: "assignee", header: "Assigned to", render: (g) => {
                const name = g.userId ? `${g.userId.firstName || ""} ${g.userId.lastName || ""}`.trim() : "Unknown";
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar user={g.userId} name={name} size={26} />
                    <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
                  </div>
                );
              } },
              { key: "category", header: "Category", render: (g) => <Badge tone="neutral">{g.category || "N/A"}</Badge> },
              { key: "metric", header: "Metric", render: (g) => <span style={{ color: t.c.textSecondary }}>{g.metric || "N/A"}</span> },
              { key: "weight", header: "Weight", align: "right", render: (g) => <span style={{ fontWeight: 600, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{g.weightage || g.weight || 0}%</span> },
              { key: "status", header: "Status", render: (g) => <Badge tone={goalStatusTone(g.status)}>{g.status || "N/A"}</Badge> },
              { key: "cycle", header: "Cycle", render: (g) => (
                <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>
                  {g.cycleId ? `${g.cycleId.month}/${g.cycleId.year}` : "N/A"}
                  {g.cycleId?.status && <span style={{ marginLeft: 6, color: g.cycleId.status === "active" ? t.status.positive : t.c.textTertiary }}>({g.cycleId.status})</span>}
                </span>
              ) },
            ]}
          />
        </Card>
      </TabPanel>

      {/* Analytics tab — reuses the shared ProjectAnalytics (theme-aware). */}
      <TabPanel activeKey={tab} tabKey="analytics">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease: t.motion.ease.out }}>
          <ProjectAnalytics project={project} />
        </motion.div>
      </TabPanel>
    </PageShell>
  );
}

function MetaCard({ t, icon, label, value, accent }) {
  return (
    <Card interactive padding={18} style={{ position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 110, height: 110, borderRadius: "50%", background: `${accent}14`, filter: "blur(20px)", pointerEvents: "none" }} />
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 14 }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: 13, background: `linear-gradient(135deg, ${accent}22, ${accent}0D)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}26` }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: t.font.size.xl, fontWeight: 700, color: t.c.text, lineHeight: 1.1, letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
          <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 4, fontWeight: 500 }}>{label}</div>
        </div>
      </div>
    </Card>
  );
}
